import { Application } from "../Application";
import { ApplicationBase } from "../ApplicationBase";
import { ApplicationTypes } from "../constants/ApplicationTypes";
import { v4 as uuidv4 } from "uuid";
import { PythonApplication } from "../PythonApplication";
import { PythonApplicationLoadSource } from '../constants';
import { createDefaultSourceFile } from '../constants';
import { GeneratedMachineAppApplication } from "../GeneratedMachineAppApplication";
import { getMachineLogicConfiguration } from "../MachineLogicConfiguration";

/**
 * @typedef {import('../Library').Library} Library
 */

/**
 * Create an Application from JSON.
 *
 * @param {Library} pLibrary MachineLogic library
 * @param {object} pAppJson
 * @returns {ApplicationBase}
 */
export function fromJson(pLibrary, pAppJson) {
  switch (pAppJson.type) {
    case ApplicationTypes.Python:
      return PythonApplication.fromJson(pLibrary, pAppJson);
    case ApplicationTypes.GeneratedMachineApp:
      return GeneratedMachineAppApplication.fromJson(pLibrary, pAppJson);
    case ApplicationTypes.MachineLogic:
    default:
      return Application.fromJson(pLibrary, pAppJson);
  }
}

export function create(pLibrary, pType) {
  switch (pType) {
    case ApplicationTypes.Python:
      return PythonApplication.fromJson(pLibrary, createPythonAppJson(pLibrary));
    case ApplicationTypes.GeneratedMachineApp:
      return GeneratedMachineAppApplication.fromJson(pLibrary, createTemporaryAppJson(pLibrary));
    case ApplicationTypes.MachineLogic:
    default:
      return Application.fromJson(pLibrary, createMachineLogicAppJson(pLibrary));
  }
}

export function createJson(pLibrary, pType) {
  switch (pType) {
    case ApplicationTypes.Python:
      return createPythonAppJson(pLibrary);
    case ApplicationTypes.GeneratedMachineApp:
      return createTemporaryAppJson(pLibrary);
    case ApplicationTypes.MachineLogic:
    default:
      return createMachineLogicAppJson(pLibrary);
  }
}

function createAppBaseJson(pLibrary, pType) {
  return {
    type : pType,
    version : pLibrary.getLibraryVersion(),
    name : pLibrary.uniqueApplicationName('New application'),
    id:  uuidv4()
  };
}

function createMachineLogicAppJson(pLibrary) {
  return {
    ...createAppBaseJson(pLibrary, ApplicationTypes.MachineLogic),
    sequences : [
      {
        name : 'Main program',
        instructions : [],
        robotParameters: {
          clips: [],
          arrays: [],
          calibrationParts: [],
        }
      }
    ],
    initialSequence : 'Main program',
    breakpoints : {},
    parameters: [],
    robotParameters: {
      clips: [],
      arrays: [],
      calibrationParts: [],
    }
  };
}

function createTemporaryAppJson(pLibrary) {
  return {
    ...createMachineLogicAppJson(pLibrary),
    type: ApplicationTypes.GeneratedMachineApp
  };
}

function createPythonAppJson(pLibrary) {
  return {
    ...createAppBaseJson(pLibrary, ApplicationTypes.Python),
    loadSource: PythonApplicationLoadSource.SingleFile,
    singleSourceFile: createDefaultSourceFile(pLibrary, getMachineLogicConfiguration().pythonDocumentationUrl)
  };
}
